import { useStore } from '@vromo/mobx-react/mobx-react';
import TrackingLinkStore from '@vromo/tracking-link/TrackingLinkStore';
import Icon from '@vromo/ui/components/FontAwesome';
import { byMostRecent } from '@vromo/utils/arrayUtils';
import { format } from 'date-fns';
import { css } from 'emotion';
import { observer } from 'mobx-react-lite';
import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { qaLabelTrackingLink } from '../../../../../../../common-qa-labels/tracking-link';
import { buildDynamicLabel } from '../../../../../../../common-qa-labels/utils';
import { getOrderSteps } from '../../../../useUIState';
import styles from './stepsDropdown.style';

const StepsDropdown = observer(({ stepIx }: { stepIx: number }): ReactElement => {
    const [showingSteps, setShowingSteps] = useState<boolean>(true);
    const { t } = useTranslation();
    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const job = rootStore.jobs.first()!;
    const orderSteps = getOrderSteps(t);

    const firstDispatch = job?.dispatches
        ?.filter((d) => d.type === 'dispatched')
        .sort(byMostRecent)[0];

    const underwayDispatch = job?.dispatches
        ?.filter((d) => d.type === 'underway')
        .sort(byMostRecent)[0];

    const timestampArray = useMemo(() => {
        const tasks = job?.tasks?.items;
        if (!tasks) return [];
        const timestamps = [];
        timestamps.push(job.created);
        timestamps.push(firstDispatch?.created || null);
        timestamps.push(underwayDispatch?.created || null);
        timestamps.push(tasks[0]?.completed?.time);
        timestamps.push(tasks[1]?.completed?.time);
        timestamps.push(tasks[2]?.completed?.time);
        timestamps.push(job?.finished);
        return timestamps;
    }, [job, stepIx]);

    const completeSteps = [...orderSteps].reverse();
    const timestampSteps = [...timestampArray].reverse();

    return (
        <div style={styles.container}>
            <div
                style={styles.titleRow}
                onClick={() => setShowingSteps(!showingSteps)}
                data-testid={qaLabelTrackingLink.orderProgressDropdownButton}>
                <div style={styles.title} data-testid={qaLabelTrackingLink.orderProgressTitle}>
                    Order Progress
                </div>
                <Icon
                    className={css`
                        transition: transform 0.3s ease;
                        transform: rotate(${showingSteps ? '0deg' : '180deg'});
                    `}
                    icon={'chevron-up'}
                />
            </div>

            <div
                style={{ ...styles.stepsContainer, maxHeight: showingSteps ? 500 : 0 }}
                data-testid={qaLabelTrackingLink.orderProgressListDiv}>
                {job.cancelled && job?.finished && (
                    <div style={styles.stepItem}>
                        <div style={styles.stepNode} />
                        <div style={styles.step}>{t('cancelled')}</div>
                        <div style={styles.time}>{format(job?.finished, 'h:mma')}</div>
                    </div>
                )}
                {completeSteps.map(
                    (s, i) =>
                        orderSteps.length - 1 - i < stepIx + 1 && (
                            <div key={s.statusTitle} style={styles.stepItem}>
                                <div style={styles.stepNode} />
                                <div
                                    data-testid={buildDynamicLabel(
                                        qaLabelTrackingLink.orderProgressItemText,
                                        `${orderSteps.length - 1 - i}`
                                    )}
                                    style={styles.step}>
                                    {s.statusTitle}
                                </div>
                                <div
                                    style={styles.time}
                                    data-testid={buildDynamicLabel(
                                        qaLabelTrackingLink.orderProgressItemTime,
                                        `${orderSteps.length - 1 - i}`
                                    )}>
                                    {timestampSteps[i] ? format(timestampSteps[i], 'h:mma') : ''}
                                </div>
                            </div>
                        )
                )}
                <div style={styles.sideLine} />
            </div>
        </div>
    );
});

export default StepsDropdown;
